import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { FetchLocationsQueryParams } from '@/api/v4/locations.api';
import { fetchLocations } from '@/api/v4/locations.api';
import { QueryKey } from '@/config/query-client';

interface StoredLocation {
  id: string;
  shortDescription?: string;
  type?: string;
}

export interface HydratedLocation {
  label: string;
  value: string;
}

const toLocationTypeFilter = (
  type: string | undefined,
): FetchLocationsQueryParams['type'] => {
  if (!type) {
    return undefined;
  }
  return [type] as FetchLocationsQueryParams['type'];
};

export const useLocationHydration = (locations: unknown) => {
  const queryClient = useQueryClient();

  const parsedList = useMemo((): StoredLocation[] | null => {
    if (!Array.isArray(locations) || locations.length === 0) {
      return null;
    }
    return locations as StoredLocation[];
  }, [locations]);

  const serialized = useMemo(() => {
    if (!parsedList) {
      return '';
    }
    return JSON.stringify(
      parsedList.map(location => ({
        id: location.id,
        shortDescription: location.shortDescription,
        type: location.type,
      })),
    );
  }, [parsedList]);

  return useQuery({
    queryKey: [QueryKey.Locations, 'hydrate', serialized],
    queryFn: async (): Promise<HydratedLocation[]> => {
      const list = JSON.parse(serialized) as StoredLocation[];

      const fetchCached = (params: FetchLocationsQueryParams) =>
        queryClient.fetchQuery({
          queryKey: [QueryKey.Locations, params],
          queryFn: () => fetchLocations(params),
        });

      const hydrateOne = async (
        location: StoredLocation,
      ): Promise<HydratedLocation> => {
        if (location.shortDescription) {
          return {
            label: location.shortDescription,
            value: JSON.stringify({
              id: location.id,
              type: location.type,
            }),
          };
        }

        try {
          const searchResults = await fetchCached({
            page: 1,
            search: location.id,
            type: location.type
              ? toLocationTypeFilter(location.type)
              : (['continent', 'country'] as const),
          });
          let foundLocation = searchResults.find(l => l.id === location.id);

          if (!foundLocation) {
            const allResults = await fetchCached({
              page: 1,
              search: '',
              type: location.type
                ? toLocationTypeFilter(location.type)
                : undefined,
            });
            foundLocation = allResults.find(l => l.id === location.id);
          }

          return {
            label: foundLocation?.shortDescription || location.id,
            value: JSON.stringify({
              id: location.id,
              type: location.type,
            }),
          };
        } catch {
          return {
            label: location.id,
            value: JSON.stringify({
              id: location.id,
              type: location.type,
            }),
          };
        }
      };

      return Promise.all(list.map(location => hydrateOne(location)));
    },
    enabled: Boolean(parsedList),
  });
};
